import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, MapPin, Phone, CheckCircle, Star } from 'lucide-react';

export const ElkGrove = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
              <MapPin className="inline w-4 h-4 mr-1" /> Serving Elk Grove, CA
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Elk Grove SEO Services & Affordable Website Design
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Geokath Marketing provides expert SEO services and affordable websites for Elk Grove businesses. 
              Help your local business get found online by Elk Grove customers searching for your services.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button size="lg" className="bg-navy hover:bg-navy-dark text-white px-8">
                  Get Free Elk Grove SEO Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <a href="tel:909-800-5616">
                <Button size="lg" variant="outline" className="border-blue-600 text-blue-600">
                  <Phone className="mr-2" size={20} />
                  Call (909) 800-5616
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Elk Grove Businesses Need SEO */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Why Elk Grove Businesses Need Local SEO
            </h2>
            <div className="prose prose-lg max-w-none text-gray-600">
              <p>
                Elk Grove is one of the fastest-growing cities in California, with over 176,000 residents and thousands of local businesses competing for attention. When Elk Grove customers search for services like yours, will they find you or your competitors?
              </p>
              <p>
                <strong>Local SEO for Elk Grove businesses</strong> ensures your company appears when nearby customers search on Google, Google Maps, and voice assistants. Without proper optimization, you're invisible to the customers actively looking for what you offer.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mt-12">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Elk Grove SEO Services Include:</h3>
                <ul className="space-y-3">
                  {[
                    'Google Business Profile optimization',
                    'Elk Grove local keyword targeting',
                    'Local citation building',
                    'Review management strategy',
                    'Mobile-friendly website optimization',
                    'Content marketing for local searches'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Affordable Websites for Elk Grove:</h3>
                <ul className="space-y-3">
                  {[
                    'Professional design for $1,500',
                    'Mobile-responsive layouts',
                    'SEO-optimized from day one',
                    'Fast loading speeds',
                    'Contact forms & lead capture',
                    'Google Analytics integration'
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle className="text-green-500 flex-shrink-0 mt-1" size={18} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Elk Grove Industries We Serve
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { name: 'Restaurants & Food Service', desc: 'Get found when Elk Grove residents search for dining options' },
                { name: 'Home Services', desc: 'Plumbers, electricians, HVAC contractors serving Elk Grove homes' },
                { name: 'Healthcare & Dental', desc: 'Connect with patients searching for local providers' },
                { name: 'Retail & Shopping', desc: 'Drive foot traffic from local shoppers' },
                { name: 'Professional Services', desc: 'Lawyers, accountants, consultants in Elk Grove' },
                { name: 'Auto Services', desc: 'Mechanics, dealerships, auto body shops' }
              ].map((industry, idx) => (
                <div key={idx} className="bg-white p-5 rounded-lg shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-2">{industry.name}</h3>
                  <p className="text-sm text-gray-600">{industry.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Grow Your Elk Grove Business?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Get a free SEO audit and discover how we can help your Elk Grove business attract more local customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8">
                Get Free Quote
              </Button>
            </Link>
            <a href="tel:909-800-5616">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-700 px-8">
                Call (909) 800-5616
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ElkGrove;
